import { Button, useDataProvider, useNotify } from "react-admin";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useField } from "react-final-form";
import { cloneDeep, debounce, find } from "lodash";
import SanitizedGrid from "../../../components/SanitizedGrid";
import { SchoolIdSelected } from "../../../utils/tools";
import {
  getFixedLocationDropOffBySchoolIds,
  removeSchoolLocationFromRouteLocation,
  reOptimiseRouteLocation,
} from "../tools";
import Autocomplete from "@material-ui/lab/Autocomplete";
import AiLoading from "./AiLoading";

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
      width: "100%",
    },
  },
  studentSearchInput: {
    marginTop: "8px",
    paddingBottom: "3px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#EAEAEA",
      },
      "&:hover fieldset": {
        borderColor: "#EAEAEA",
      },
    },
  },
});

interface AddLocationButtonProps {
  school: ISchool;
  busSchedule: IBusSchedule;
  maxStopTime: number;
  routes: IRoute[];
}

const AddLocationButton: FC<AddLocationButtonProps> = ({
  busSchedule,
  school,
  maxStopTime,
  routes,
}) => {
  const [students, setStudents] = useState<IStudent[]>([]);
  const [studentAddresses, setStudentAddresses] = useState<IStudentAddress[]>(
    [],
  );
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [selectedStudentAddress, setSelectedStudentAddress] =
    useState<IStudentAddress>(null);

  const [isDisable, setIsDisable] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isHaveDuplicateStudent, setIsHaveDuplicateStudent] =
    useState<boolean>(false);

  const routeLocationsField = useField("routeLocations");
  const routeGeometriesField = useField(`routeGeometries`);

  const classes = useStyles();
  const provider = useDataProvider();
  const notify = useNotify();
  const schoolIdSelected = SchoolIdSelected();

  const handleOpenDialogAddLocation = () => {
    setOpen(true);
  };

  const handleCloseDialogAddLocation = () => {
    setOpen(false);
  };

  const handleOpenDialogDuplicateStudent = () => {
    setIsHaveDuplicateStudent(true);
  };

  const handleCloseDialogDuplicateStudent = () => {
    setIsHaveDuplicateStudent(false);
  };

  const handleSaveAddLocation = async () => {
    try {
      setIsLoading(true);

      const newRouteLocation: IRouteLocation = {
        id: 0,
        address: `${selectedStudentAddress.blockNumber} ${selectedStudentAddress.street} ${selectedStudentAddress.building} ${selectedStudentAddress.postalCode}`,
        blockNumber: selectedStudentAddress.blockNumber,
        building: selectedStudentAddress.building,
        postalCode: selectedStudentAddress.postalCode,
        street: selectedStudentAddress.street,
        unitNumber: selectedStudentAddress.unitNumber,
        lat: selectedStudentAddress.lat,
        lng: selectedStudentAddress.lng,
        order: 0,
        remark: null,
        arrivalTime: null,
        students: [selectedStudent],
      };

      const currentLocations = cloneDeep(routeLocationsField.input.value);

      const insertIndex = currentLocations.length - 1;
      const updatedRouteLocations = [
        ...currentLocations.slice(0, insertIndex),
        newRouteLocation,
        ...currentLocations.slice(insertIndex),
      ];

      const routeLocationWithoutSchool = removeSchoolLocationFromRouteLocation(
        updatedRouteLocations,
        busSchedule.type,
      );

      const optimisedRouteLocations = await reOptimiseRouteLocation(
        school,
        busSchedule,
        maxStopTime,
        routeLocationWithoutSchool,
      );

      routeLocationsField.input.onChange(optimisedRouteLocations);
      routeGeometriesField.input.onChange([]);

      setIsLoading(false);
      handleCloseDialogAddLocation();
      handleCloseDialogDuplicateStudent();
    } catch (error) {
      notify("Failed to add new location", "error");
      setIsLoading(false);
    }
  };

  const handleCheckDuplicateStudent = async () => {
    setIsLoading(true);

    const duplicateStudentId = routes.find((route: any) =>
      route.routeLocations.find((routeLocation: any) =>
        routeLocation.students.find(
          (student: any) => student.id === selectedStudent.id,
        ),
      ),
    );

    if (duplicateStudentId) {
      handleOpenDialogDuplicateStudent();
      setIsLoading(false);
    } else {
      await handleSaveAddLocation();
    }
  };

  const getStudents = useCallback(
    debounce((searchString: string) => {
      const filter: any = { SchoolId: schoolIdSelected, Status: "active" };

      if (busSchedule.type === "cca") {
        filter.HasCcaSchedule = true;
      }

      if (
        busSchedule.type === "to_school" ||
        busSchedule.type === "from_school"
      ) {
        filter.HasRegularSchedule = true;
      }

      provider
        .customGetListWithStaticParams(
          `students?filter=${JSON.stringify(
            filter,
          )}&pageSize=50&search=${searchString}`,
        )
        .then((result: any) => {
          setStudents(result.data as IStudent[]);
        });
    }, 1000),
    [busSchedule.type],
  );

  const getStudentAddress = async () => {
    try {
      let _studentAddress = await provider.getList("studentAddresses", {
        filter: { StudentId: Number(selectedStudent.id) },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "Id",
          order: "ASC",
        },
      });
      let dataFixLocation: any = [];

      if (busSchedule.type === "cca") {
        dataFixLocation = await getFixedLocationDropOffBySchoolIds(
          schoolIdSelected,
          provider,
        );
      }

      setStudentAddresses([..._studentAddress.data, ...dataFixLocation]);
    } catch (error) {
      setStudentAddresses([]); // Fallback to empty array
    }
  };

  useEffect(() => {
    if (searchValue !== "") {
      getStudents(searchValue);
    }
  }, [searchValue]);

  useEffect(() => {
    if (selectedStudent !== null) {
      getStudentAddress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudent]);

  useEffect(() => {
    if (selectedStudent !== null && selectedStudentAddress !== null) {
      setIsDisable(false);
    } else {
      setIsDisable(true);
    }
  }, [selectedStudent, selectedStudentAddress]);

  return (
    <>
      <Button label="Add Location" onClick={handleOpenDialogAddLocation} />

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">Add Location</DialogTitle>
        <DialogContent>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Select Student</Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                className={classes.studentSearchInput}
                freeSolo
                disableClearable
                options={students.map((option: IStudent) => {
                  return `${option.id} - ${option.firstName} ${option.lastName}`;
                })}
                onChange={(_event, value) => {
                  const splitedValue = value.split(" ");

                  setSelectedStudent(
                    find(students, ["id", Number(splitedValue[0])]),
                  );
                }}
                onInputChange={(_event, newInputValue) => {
                  setSearchValue(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Student"
                    variant="outlined"
                    InputProps={{ ...params.InputProps, type: "search" }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2">
                Select Student address
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                className={classes.studentSearchInput}
                freeSolo
                disableClearable
                options={studentAddresses.map((option: any) => {
                  let type = "";
                  if (option.isMain === true) {
                    type = "MAIN";
                  } else if (option.isMain === false) {
                    type = "SUB";
                  } else {
                    type = option.name;
                  }
                  return `${option.id} - ${option.blockNumber} ${option.street} ${option.postalCode} - ${type}`;
                })}
                onChange={(_event, value) => {
                  const splitedValue = value.split(" ");

                  setSelectedStudentAddress(
                    find(studentAddresses, ["id", Number(splitedValue[0])]),
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Student Address"
                    variant="outlined"
                    InputProps={{ ...params.InputProps, type: "search" }}
                  />
                )}
              />
            </Grid>
          </SanitizedGrid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialogAddLocation}
            color="secondary"
            label="Back"
          />
          <Button
            onClick={handleCheckDuplicateStudent}
            color="primary"
            label="Save"
            disabled={isDisable}
            startIcon={
              isLoading && (
                <CircularProgress size={20} style={{ color: "#FFFFFF" }} />
              )
            }
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={isHaveDuplicateStudent}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">
          Info Duplicate Student
        </DialogTitle>
        <DialogContent>
          <DialogContentText color="initial">
            The student that you are trying to add has already been added to
            this wave. do you want to add the student again?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialogDuplicateStudent}
            color="secondary"
            label="Back"
          />
          <Button
            onClick={handleSaveAddLocation}
            color="primary"
            label="Confirm"
            disabled={isDisable}
            startIcon={
              isLoading && (
                <CircularProgress size={20} style={{ color: "#FFFFFF" }} />
              )
            }
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={isLoading}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
      >
        <AiLoading isLoading={isLoading} />
      </Dialog>
    </>
  );
};

export default AddLocationButton;
